import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { PLANNER_MATCH_CTA_LABEL } from "@/constants/plannerMatching";
import theme from "@/theme";

import MatchingCheckImage from "@assets/image/matching_check.png";
import MatchingConfirmCharacterImage from "@assets/image/matching_confirm_character.png";

import { CustomButton } from "../Button";
import { StyledModal } from "../StyledModal";

export const MATCH_CONFIRM_TITLE = "こちらのプランナーに\n相談しますか？";
// Figma B-3 に合わせて2文は改行せず続けて流す
export const MATCH_CONFIRM_DESCRIPTION =
  "プランナーに相談することで、ご自身に合った物件や資金計画を具体的に整理できます。まずは情報収集として、お気軽にお話しください。";
export const MATCH_CONFIRM_NOTE = "※マッチング後、担当プランナーから日程調整のご連絡を差し上げます。";
export const MATCH_CONFIRM_SUBMIT_LABEL = "はい";
export const MATCH_CONFIRM_CANCEL_LABEL = "いいえ";
// プランナー詳細（variant="planner"）の文言。Figma「施策_マッチ確認モーダル」（2026-10-27）に合わせ、
// 主ボタンは画面下部の CTA と同じ「話を聞いてみる」（PLANNER_MATCH_CTA_LABEL）で揃える。日程調整の注記は成立後のモーダルで案内するため出さない
export const MATCH_CONFIRM_PLANNER_TITLE = "こちらのプランナーに\n話を聞いてみますか？";
export const MATCH_CONFIRM_PLANNER_SUBMIT_LABEL = PLANNER_MATCH_CTA_LABEL;
export const MATCH_CONFIRM_PLANNER_CANCEL_LABEL = "やめる";

const useStyles = makeStyles(() => ({
  // --- プランナー詳細（Figma「施策_マッチ確認モーダル」）。SP のみのデザインなので PC 幅の余白・文字サイズは従来の上書きを維持する
  plannerContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    padding: 24,
    [theme.breakpoints.up("md")]: { padding: 32, gap: 24 },
  },
  // 2倍書き出し（400×240）の PNG を 200×120 で表示する
  plannerImage: { display: "block", width: 200, height: 120 },
  plannerHead: { display: "flex", flexDirection: "column", gap: 8, width: "100%", color: theme.palette.text.primary },
  plannerTitle: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5,
    textAlign: "center",
    // 定数内の改行で2行に分けて表示する
    whiteSpace: "pre-line",
    [theme.breakpoints.up("md")]: { fontSize: 20 },
  },
  plannerDescription: { fontSize: 14, fontWeight: 400, lineHeight: 1.5, textAlign: "left" },
  plannerActions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    width: "100%",
    "& button": { width: "100%", [theme.breakpoints.up("md")]: { width: 360, maxWidth: "100%" } },
  },
  // --- 旧レイアウト（variant="legacy"）
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3, 2.5),
  },
  image: {
    width: 200,
    height: "auto",
    marginBottom: theme.spacing(1),
  },
  title: {
    color: theme.palette.primary.main,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.4,
    textAlign: "center",
    // 定数内の改行で2行に分けて表示する
    whiteSpace: "pre-line",
  },
  description: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.6,
    textAlign: "left",
    marginTop: theme.spacing(2),
  },
  note: {
    width: "100%",
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.5,
    textAlign: "left",
    marginTop: theme.spacing(1.5),
  },
  submitButtonWrap: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  cancelLinkContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 30,
    marginTop: theme.spacing(2),
  },
  cancelLink: {
    textDecoration: "none",
    cursor: "pointer",
    textAlign: "center",
    minWidth: 100,
  },
}));

interface MatchConfirmModalProps {
  // マッチングを実行する。非同期の場合は完了まで実行ボタンを押せなくする
  onConfirm: () => void | Promise<void>;
  variant?: "legacy" | "planner";
  // 何もせず閉じる
  onCancel: () => void;
}

// ユーザーオファー（「話を聞いてみる」）の送信直前に挟む確認モーダル。
// マッチは取り消せず、プランナーへの通知メールも飛ぶため、誤タップでの成立を防ぐのが目的。
// プランナーオファー（投資/査定提案）の承認と msins 系は対象外（従来どおり確認なしで実行）
export const MatchConfirmModal: React.FC<MatchConfirmModalProps> = ({ onConfirm, onCancel, variant = "legacy" }) => {
  const classes = useStyles();
  const [isConfirming, setIsConfirming] = React.useState(false);
  // disabled は state 反映を待つため、同一レンダー内の連打には間に合わない。
  // マッチとプランナー宛メールの二重発生を防ぐため ref でも弾く
  const isConfirmingRef = React.useRef(false);
  // 実行が終わる前にモーダルが閉じられる（＝成功時は呼び出し側が閉じる）ため、
  // アンマウント後の setState を避ける。React 17 では console.error 警告になり
  // src/index.tsx の CaptureConsole 経由で Sentry イベントになる
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleConfirm = React.useCallback(async () => {
    if (isConfirmingRef.current) return;
    isConfirmingRef.current = true;
    setIsConfirming(true);

    try {
      await onConfirm();
    } catch {
      // 失敗の通知は呼び出し側の責務。ここで投げ直すと async ハンドラの
      // unhandled rejection になるため受け止めるだけにする
    } finally {
      // 失敗しても押せる状態に戻す。ここを抜かすとマッチできないまま詰む。
      // ref の更新は副作用が無くアンマウント後でも害がないためガードしない
      isConfirmingRef.current = false;
      if (mountedRef.current) setIsConfirming(false);
    }
  }, [onConfirm]);

  // 実行中の誤タップ・外側クリックでは閉じない（成功時は呼び出し側が閉じる）
  const handleCancel = () => {
    if (!isConfirmingRef.current) onCancel();
  };

  const isPlanner = variant === "planner";
  const title = isPlanner ? MATCH_CONFIRM_PLANNER_TITLE : MATCH_CONFIRM_TITLE;

  return (
    <StyledModal onClickOutside={handleCancel} role="dialog" ariaLabel={title.replace("\n", "")}>
      {isPlanner ? (
        <Box className={classes.plannerContent}>
          {/* 相談への前向きな入口として見せるための専用イラスト。装飾扱いなので alt は空にし、見出しは dialog の aria-label で読ませる */}
          <img src={MatchingConfirmCharacterImage} alt="" className={classes.plannerImage} />
          <Box className={classes.plannerHead}>
            <Typography component="h2" className={classes.plannerTitle}>
              {MATCH_CONFIRM_PLANNER_TITLE}
            </Typography>
            <Typography className={classes.plannerDescription}>{MATCH_CONFIRM_DESCRIPTION}</Typography>
          </Box>
          {/* 戻る導線は主ボタンとの視覚的な強弱をつけるためテキストボタンにする */}
          <Box className={classes.plannerActions}>
            <CustomButton tone="trust" buttonSize="L" fullWidth onClick={handleConfirm} disabled={isConfirming}>
              {MATCH_CONFIRM_PLANNER_SUBMIT_LABEL}
            </CustomButton>
            <CustomButton tone="text" buttonSize="M" fullWidth disabled={isConfirming} onClick={handleCancel}>
              {MATCH_CONFIRM_PLANNER_CANCEL_LABEL}
            </CustomButton>
          </Box>
        </Box>
      ) : (
        <Box className={classes.content}>
          {/* 相談への前向きな入口として見せるための専用イラスト */}
          <img src={MatchingCheckImage} alt="プランナーとの相談" className={classes.image} />
          <Typography className={classes.title}>{MATCH_CONFIRM_TITLE}</Typography>
          <Typography className={classes.description}>{MATCH_CONFIRM_DESCRIPTION}</Typography>
          <Typography className={classes.note}>{MATCH_CONFIRM_NOTE}</Typography>
          <Box className={classes.submitButtonWrap}>
            <CustomButton
              buttonSize="L"
              fullWidth
              customVariant="filled"
              onClick={handleConfirm}
              disabled={isConfirming}
            >
              {MATCH_CONFIRM_SUBMIT_LABEL}
            </CustomButton>
          </Box>
          {/* 戻る導線は主ボタンとの視覚的な強弱をつけるためテキストリンクにする（ApproveModal の「閉じる」と同じ扱い）。
              実行中の誤タップは onClick 側のガードで無効化する */}
          <Box className={classes.cancelLinkContainer}>
            <Typography
              variant="button"
              color="primary"
              className={classes.cancelLink}
              onClick={() => {
                if (isConfirming) return;
                onCancel();
              }}
            >
              {MATCH_CONFIRM_CANCEL_LABEL}
            </Typography>
          </Box>
        </Box>
      )}
    </StyledModal>
  );
};
